import React from 'react';
import { Smartphone, Tablet, Monitor, Eye, EyeOff, X } from 'lucide-react';

/**
 * Breakpoint definitions for visibility control
 */
const BREAKPOINTS = [
  { id: 'base', label: 'Mobile', icon: Smartphone },
  { id: 'tablet', label: 'Tablet', icon: Tablet },
  { id: 'desktop', label: 'Desktop', icon: Monitor },
];

/**
 * ResponsiveVisibilityField - Toggle visibility per breakpoint
 *
 * Allows users to control element visibility at different screen sizes.
 * Uses a mobile-first inheritance model where tablet inherits from base,
 * and desktop inherits from tablet (or base if tablet is null).
 *
 * Data structure:
 * {
 *   base: true,      // true = visible, false = hidden (mobile/base)
 *   tablet: null,    // null = inherit from base, true/false = override
 *   desktop: null    // null = inherit from tablet/base, true/false = override
 * }
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {Object} [props.field.default] - Default responsive visibility
 * @param {Object} props.value - Current value { base, tablet, desktop }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 */
export default function ResponsiveVisibilityField({
  field,
  value,
  onChange,
  disabled = false,
  compact = false,
}) {
  /**
   * Normalize value to ensure proper responsive format
   */
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null && val.base !== undefined) {
      return {
        base: val.base ?? true,
        tablet: val.tablet ?? null,
        desktop: val.desktop ?? null,
      };
    }
    // If simple boolean, convert to responsive format
    if (typeof val === 'boolean') {
      return {
        base: val,
        tablet: null,
        desktop: null,
      };
    }
    // Default value
    return field.default || {
      base: true,
      tablet: null,
      desktop: null,
    };
  };

  const responsiveValue = normalizeValue(value);

  /**
   * Get the effective visibility for a breakpoint (resolving inheritance)
   */
  const getEffectiveVisibility = (breakpointId) => {
    if (breakpointId === 'base') {
      return responsiveValue.base;
    }
    if (breakpointId === 'tablet') {
      return responsiveValue.tablet !== null ? responsiveValue.tablet : responsiveValue.base;
    }
    if (breakpointId === 'desktop') {
      if (responsiveValue.desktop !== null) {
        return responsiveValue.desktop;
      }
      if (responsiveValue.tablet !== null) {
        return responsiveValue.tablet;
      }
      return responsiveValue.base;
    }
    return true;
  };

  /**
   * Check if a breakpoint has an override (non-null value for non-base)
   */
  const hasOverride = (breakpointId) => {
    if (breakpointId === 'base') return false;
    return responsiveValue[breakpointId] !== null;
  };

  /**
   * Toggle visibility for a specific breakpoint
   */
  const toggleVisibility = (breakpointId) => {
    const currentEffective = getEffectiveVisibility(breakpointId);
    const newValue = !currentEffective;

    if (breakpointId === 'base') {
      // Base always sets explicit value
      onChange({
        ...responsiveValue,
        base: newValue,
      });
    } else {
      // Non-base breakpoints set override
      onChange({
        ...responsiveValue,
        [breakpointId]: newValue,
      });
    }
  };

  /**
   * Clear override for a breakpoint (reset to inherit)
   */
  const clearOverride = (breakpointId, e) => {
    e.stopPropagation();
    if (breakpointId !== 'base') {
      onChange({
        ...responsiveValue,
        [breakpointId]: null,
      });
    }
  };

  /**
   * Get inheritance source label for a breakpoint
   */
  const getInheritanceSource = (breakpointId) => {
    if (breakpointId === 'base') return null;
    if (breakpointId === 'tablet') return 'Mobile';
    if (breakpointId === 'desktop') {
      return responsiveValue.tablet !== null ? 'Tablet' : 'Mobile';
    }
    return null;
  };

  // Compact mode layout
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-0.5">
        {field.label && (
          <label className="text-xs text-gray-500 w-16 flex-shrink-0 truncate">
            {field.label}
          </label>
        )}
        <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
          {BREAKPOINTS.map((bp) => {
            const BreakpointIcon = bp.icon;
            const isVisible = getEffectiveVisibility(bp.id);
            const isOverridden = hasOverride(bp.id);
            const isInherited = bp.id !== 'base' && !isOverridden;

            return (
              <button
                key={bp.id}
                type="button"
                onClick={() => toggleVisibility(bp.id)}
                disabled={disabled}
                title={`${bp.label}: ${isVisible ? 'Visible' : 'Hidden'}${isInherited ? ' (inherited)' : ''}`}
                className={`
                  relative flex items-center gap-1 px-1.5 py-1 rounded transition-colors
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  ${isOverridden
                    ? isVisible
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'bg-white shadow-sm text-red-600'
                    : isInherited
                      ? 'text-gray-400'
                      : isVisible
                        ? 'bg-white shadow-sm text-gray-900'
                        : 'bg-white shadow-sm text-red-600'
                  }
                `}
              >
                <BreakpointIcon className="w-3 h-3" />
                {isVisible ? (
                  <Eye className={`w-3 h-3 ${isInherited ? 'opacity-50' : ''}`} />
                ) : (
                  <EyeOff className={`w-3 h-3 ${isInherited ? 'opacity-50' : ''}`} />
                )}
                {isOverridden && (
                  <span
                    onClick={(e) => clearOverride(bp.id, e)}
                    className="absolute -top-1 -right-1 w-3 h-3 bg-gray-400 hover:bg-gray-600 rounded-full flex items-center justify-center cursor-pointer"
                    title={`Clear ${bp.label} override`}
                  >
                    <X className="w-2 h-2 text-white" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Standard layout
  return (
    <div className="space-y-2">
      {field.label && (
        <label className="block text-xs font-medium text-gray-700">
          {field.label}
        </label>
      )}
      <div className="flex items-center gap-1 p-1 bg-gray-100 rounded">
        {BREAKPOINTS.map((bp) => {
          const BreakpointIcon = bp.icon;
          const isVisible = getEffectiveVisibility(bp.id);
          const isOverridden = hasOverride(bp.id);
          const isInherited = bp.id !== 'base' && !isOverridden;
          const inheritSource = getInheritanceSource(bp.id);

          return (
            <div
              key={bp.id}
              className="flex-1 relative"
            >
              <button
                type="button"
                onClick={() => toggleVisibility(bp.id)}
                disabled={disabled}
                title={`${bp.label}: ${isVisible ? 'Visible' : 'Hidden'}${isInherited ? ` (inherits from ${inheritSource})` : ''}`}
                className={`
                  w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded transition-colors
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
                  ${isOverridden || bp.id === 'base'
                    ? isVisible
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'bg-white shadow-sm text-red-600'
                    : 'text-gray-400 hover:text-gray-600'
                  }
                `}
              >
                <BreakpointIcon className="w-4 h-4" />
                {isVisible ? (
                  <Eye className={`w-4 h-4 ${isInherited ? 'opacity-50' : ''}`} />
                ) : (
                  <EyeOff className={`w-4 h-4 ${isInherited ? 'opacity-50' : ''}`} />
                )}
              </button>
              {/* Clear override button */}
              {isOverridden && (
                <button
                  type="button"
                  onClick={(e) => clearOverride(bp.id, e)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-gray-400 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors"
                  title={`Clear ${bp.label} override (inherit from ${inheritSource})`}
                >
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-between text-[10px] text-gray-400 px-1">
        <span>Mobile</span>
        <span>Tablet</span>
        <span>Desktop</span>
      </div>
    </div>
  );
}
